import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Modal, Select, Space, Tag } from "antd";
import type { ShippingOwnerCluesVo } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { DATETIME_PICKER_FORMAT, DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import { SHIP_BIND_STATE, SHIPPING_OWNER_CLUES_STATE, TRIGGER_PAGE } from "@zhihaoscm/meta";

type ClueRow = ShippingOwnerCluesVo & { shippingOwnerClues: ShippingOwnerCluesVo["shippingOwnerClues"] };
export type ShippingOwnerClueQuery = {
    page: number;
    size: number;
    sortKey?: string;
    sortOrder?: string;
    keyWord?: string;
    existShip?: number;
    state?: number | number[];
    handlerName?: string;
    triggerPage?: number;
    beginTime?: string;
    endTime?: string;
};
export type ShippingOwnerClueUpdate = { state: number; remark: string };

export async function loadShippingOwnerClues(query: ShippingOwnerClueQuery) {
    const { shippingOwnerCluesService } = await import("@/api/services/shippingOwnerCluesService");
    return shippingOwnerCluesService.paging(query);
}

export async function updateShippingOwnerClue(id: number | string, form: ShippingOwnerClueUpdate) {
    const { shippingOwnerCluesService } = await import("@/api/services/shippingOwnerCluesService");
    return shippingOwnerCluesService.update(id, form);
}

const stateLabel = (state?: number) => ({ 1: "待处理", 2: "处理中", 3: "已处理完成" })[Number(state)] || "未知";

export interface ShippingOwnerCluesPageProps {
    loadClues?: (query: ShippingOwnerClueQuery) => Promise<{ list?: ClueRow[]; total?: number }>;
    updateClue?: (id: number | string, form: ShippingOwnerClueUpdate) => Promise<unknown>;
    permissions?: string[];
}

/** 船主线索列表：保持 Vue 的筛选字段、默认分页和处理状态。 */
export function ShippingOwnerCluesPage({
    loadClues = loadShippingOwnerClues,
    updateClue = updateShippingOwnerClue,
    permissions
}: ShippingOwnerCluesPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const canWrite = (permissions || storePermissions).includes("ROLE_SHIP_CLUE_W");
    const [rows, setRows] = useState<ClueRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ShippingOwnerClueQuery>({
        page: 1,
        size: 10,
        state: [SHIPPING_OWNER_CLUES_STATE.PENDING_PROCESSING.code, SHIPPING_OWNER_CLUES_STATE.PROCESSING.code]
    });
    const [keyword, setKeyword] = useState("");
    const [existShip, setExistShip] = useState<number | undefined>();
    const [triggerPage, setTriggerPage] = useState<number | undefined>();
    const [handlerName, setHandlerName] = useState("");
    const [state, setState] = useState<number[]>([
        SHIPPING_OWNER_CLUES_STATE.PENDING_PROCESSING.code,
        SHIPPING_OWNER_CLUES_STATE.PROCESSING.code
    ]);
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState<ClueRow | null>(null);
    const [remark, setRemark] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let active = true;
        void loadClues(query)
            .then(result => {
                if (!active) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (!active) return;
                setRows([]);
                setTotal(0);
                setError("船主线索加载失败");
            });
        return () => {
            active = false;
        };
    }, [loadClues, query]);

    // 将筛选草稿转换为旧 Vue ProTable 的后端字段，日期边界保持整日查询语义。
    const submitFilters = () => {
        const nextQuery: ShippingOwnerClueQuery = {
            page: 1,
            size: 10,
            state,
            ...(keyword.trim() ? { keyWord: keyword.trim() } : {}),
            ...(existShip === undefined ? {} : { existShip }),
            ...(triggerPage === undefined ? {} : { triggerPage }),
            ...(handlerName.trim() ? { handlerName: handlerName.trim() } : {}),
            ...(dateRange?.[0] ? { beginTime: dateFormat(dateRange[0], LOG_DATE_FORMAT_START) } : {}),
            ...(dateRange?.[1] ? { endTime: dateFormat(dateRange[1], LOG_DATE_FORMAT_END) } : {})
        };
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setKeyword("");
        setExistShip(undefined);
        setTriggerPage(undefined);
        setHandlerName("");
        setState([SHIPPING_OWNER_CLUES_STATE.PENDING_PROCESSING.code, SHIPPING_OWNER_CLUES_STATE.PROCESSING.code]);
        setDateRange(null);
        setQuery({
            page: 1,
            size: 10,
            state: [SHIPPING_OWNER_CLUES_STATE.PENDING_PROCESSING.code, SHIPPING_OWNER_CLUES_STATE.PROCESSING.code]
        });
    };
    const process = async () => {
        if (!editing || saving) return;
        setSaving(true);
        try {
            await updateClue(editing.shippingOwnerClues.id, { state: 3, remark });
            setEditing(null);
            setRemark("");
            setQuery(value => ({ ...value }));
        } catch {
            setError("线索处理失败");
        } finally {
            setSaving(false);
        }
    };

    /** 将旧 Vue 的触发时间排序转换为船主线索分页接口字段，并回到第一页。 */
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => {
            const nextQuery = { ...current, page: 1 };
            delete nextQuery.sortKey;
            delete nextQuery.sortOrder;
            if (prop === "createdTime" && order) {
                nextQuery.sortKey = "created_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue ProTable 的展示字段与操作插槽。
    const columns = useMemo<TableColumnProps<ClueRow>[]>(
        () => [
            {
                key: "id",
                label: "序号1",
                width: 124,
                prop: "shippingOwnerClues.id",
                render: ({ row }) => (
                    <Link to={`/logistics/cluesManage/shippingOwnerCluesDetail/${row.shippingOwnerClues.id}`}>
                        {row.shippingOwnerClues.id}
                    </Link>
                )
            },
            {
                key: "captain",
                label: "承运商账号",
                minWidth: 180,
                prop: "shippingOwnerClues.captainEnterprise",
                render: ({ row }) => {
                    const captain = row.shippingOwnerClues.captainEnterprise as unknown as Record<string, string>;
                    return (
                        [captain?.realName || captain?.institutionName, captain?.code].filter(Boolean).join(" | ") ||
                        "--"
                    );
                }
            },
            {
                key: "existShip",
                label: "是否绑定船舶",
                minWidth: 80,
                prop: "existShip",
                render: ({ row }) => (row.existShip ? "是" : "否")
            },
            {
                key: "ships",
                label: "船舶",
                minWidth: 200,
                prop: "ships",
                render: ({ row }) =>
                    row.ships?.length
                        ? row.ships.map(ship => (
                              <Link key={ship.id} to={`/logistics/shipManage/shipInfo/shipInfoDetail/${ship.id}`}>
                                  {ship.cnname || ship.name}
                              </Link>
                          ))
                        : "--"
            },
            {
                key: "triggerPage",
                label: "触发页面",
                width: 220,
                prop: "shippingOwnerClues.triggerPage",
                render: ({ row }) => TRIGGER_PAGE.getIntroByCode(row.shippingOwnerClues.triggerPage) || DEFAULT_VALUE
            },
            {
                key: "createdTime",
                label: "触发时间",
                width: 160,
                prop: "createdTime",
                sortable: "custom",
                render: ({ row }) =>
                    row.shippingOwnerClues.createdTime
                        ? dateFormat(row.shippingOwnerClues.createdTime, DATETIME_PICKER_FORMAT)
                        : DEFAULT_VALUE
            },
            {
                key: "mobile",
                label: "联系方式",
                width: 140,
                prop: "shippingOwnerClues.mobile",
                render: ({ row }) => row.shippingOwnerClues.mobile || "--"
            },
            {
                key: "handler",
                label: "处理人",
                width: 140,
                prop: "shippingOwnerClues.handlerName",
                render: ({ row }) => row.shippingOwnerClues.handlerName || "--"
            },
            {
                key: "state",
                label: "状态",
                width: 90,
                prop: "shippingOwnerClues.state",
                render: ({ row }) => <Tag>{stateLabel(row.shippingOwnerClues.state)}</Tag>
            },
            {
                key: "operation",
                label: "操作",
                fixed: "right",
                width: 60,
                prop: "operation",
                render: ({ row }) =>
                    canWrite && Number(row.shippingOwnerClues.state) !== 3 ? (
                        <Button
                            type="link"
                            onClick={() => {
                                setEditing(row);
                                setRemark("");
                            }}
                        >
                            处理
                        </Button>
                    ) : null
            }
        ],
        [canWrite]
    );

    return (
        <section aria-label="shipping-owner-clues-page">
            <Card title="船主线索">
                <Space wrap>
                    <Input
                        aria-label="承运商账号"
                        placeholder="请输入承运商账号信息"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    <Select
                        aria-label="是否绑定船舶"
                        allowClear
                        placeholder="是否绑定船舶"
                        value={existShip}
                        options={SHIP_BIND_STATE.options}
                        onChange={setExistShip}
                    />
                    <Select
                        aria-label="触发页面"
                        allowClear
                        placeholder="请选择触发页面"
                        value={triggerPage}
                        options={TRIGGER_PAGE.options}
                        onChange={setTriggerPage}
                    />
                    <Input
                        aria-label="处理人"
                        maxLength={50}
                        placeholder="请输入处理人"
                        value={handlerName}
                        onChange={event => setHandlerName(event.target.value)}
                    />
                    <Select
                        aria-label="状态"
                        mode="multiple"
                        allowClear
                        placeholder="请选择状态"
                        value={state}
                        options={SHIPPING_OWNER_CLUES_STATE.options}
                        onChange={setState}
                    />
                    <ZhDateRangePicker
                        aria-label="触发时间"
                        endPlaceholder="触发结束时间"
                        format="YYYY-MM-DD"
                        rangeSeparator="-"
                        startPlaceholder="触发起始时间"
                        type="daterange"
                        value={dateRange}
                        valueFormat="YYYY-MM-DD"
                        onChange={(_value, formattedValue) => setDateRange(formattedValue)}
                    />
                    <Button type="primary" onClick={submitFilters}>
                        查询
                    </Button>
                    <Button onClick={resetFilters}>重置</Button>
                </Space>
            </Card>
            {error ? <Alert message={error} type="error" showIcon /> : null}
            <Card>
                <ZhTable<ClueRow>
                    rowKey={row => String(row.shippingOwnerClues.id)}
                    data={rows}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无数据"
                    onSortChange={handleSortChange}
                    pagination={false}
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(value => ({
                                        ...value,
                                        // 切换每页条数后回到第一页，普通翻页保留当前页。
                                        page: pageSize !== value.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <Modal
                open={Boolean(editing)}
                title="处理船主线索"
                okText="确认处理"
                cancelText="取消"
                confirmLoading={saving}
                onOk={() => void process()}
                onCancel={() => {
                    if (!saving) setEditing(null);
                }}
            >
                <Input.TextArea
                    aria-label="处理备注"
                    rows={4}
                    value={remark}
                    onChange={event => setRemark(event.target.value)}
                    placeholder="请输入处理备注"
                />
            </Modal>
        </section>
    );
}

export default ShippingOwnerCluesPage;
